// UNMATCHED_MATERIAL_ROUTE — a Fulfillment escalation for material that
// never matched a job number at all ("No Job Found", escalated to Cary).
// Cross-section, like SHORTAGE_FOLLOWUP: the trigger is a live Fulfillment
// MaterialReceiptRow, not something Proxy generated on its own.
//
// There's no real job number here, so the "job" this task hangs off of IS
// the scan — a minimal stub ProxyJob authored in mockProxyJobs.ts with
// jobId === the receipt's scan_number. That keeps the shared Intake Queue /
// item-detail panel machinery (which always resolves a task against
// jobs.find(j => j.jobId === task.jobId)) working unchanged, with no new
// jobless code path to special-case.
import type { ProxyJob } from '../mockProxyJobs';
import { PROXY_SEED_TODAY } from '../mockProxyJobs';
import type { MaterialReceiptRow } from '../../types/api';
import type {
  ProxyTask, ProxyWorkflowContext, ProxyWorkflowDefinition, ProxyPanelContext, ProxyReviewPanelSchema,
} from '../proxyWorkflowTypes';

// The cross-section match: a live Fulfillment receipt for THIS scan, filed
// under Proxy (job_category 'P'), that Fulfillment couldn't match to any job
// and escalated. Mirrors shortageFollowupWorkflow's findFulfillmentEscalation,
// just matched on scan_number instead of job_numbers (there is no job here).
function findUnmatchedEscalation(job: ProxyJob, receipts: MaterialReceiptRow[] | null): MaterialReceiptRow | undefined {
  if (!receipts) return undefined;
  return receipts.find(r =>
    r.job_category === 'P' &&
    r.material_status === 'No Job Found' &&
    !!r.escalated_to_team &&
    r.scan_number === job.jobId,
  );
}

function buildTask(job: ProxyJob, receipt: MaterialReceiptRow): ProxyTask {
  return {
    id: `unmatched-${job.jobId}`,
    workflowType: 'UNMATCHED_MATERIAL_ROUTE',
    jobId: job.jobId,
    title: `${job.issuer} - Unmatched material`,
    subtitle: `Scan ${receipt.scan_number}`,
    reason: `Fulfillment couldn't find a job number for scan ${receipt.scan_number} (${job.issuer}) and escalated it to Proxy/ICT for manual matching.`,
    source: 'fulfillment_escalation',
    sourceLabel: 'Fulfillment escalation',
    sourceDetail: {
      rows: [
        { label: 'Scan #', value: receipt.scan_number },
        { label: 'Client name(s)', value: receipt.client_names.join(', ') },
        { label: 'Material status', value: receipt.material_status },
      ],
    },
    createdAt: PROXY_SEED_TODAY,
    // A confirmed system escalation, not an inferred heuristic — same
    // reasoning as SHORTAGE_FOLLOWUP's flat 97.
    confidence: 97,
  };
}

function buildTasks({ jobs, receipts }: ProxyWorkflowContext): ProxyTask[] {
  const tasks: ProxyTask[] = [];
  for (const job of jobs) {
    const receipt = findUnmatchedEscalation(job, receipts);
    if (!receipt) continue;
    tasks.push(buildTask(job, receipt));
  }
  return tasks;
}

function defaultEdits(): Record<string, any> {
  return {}; // nothing here is human-editable
}

function buildPanel({ job }: ProxyPanelContext): ProxyReviewPanelSchema {
  const subject = `Re: unmatched material - Scan ${job.jobId}`;
  const body = `Thanks for flagging this. We're researching a job match for ${job.issuer} (scan ${job.jobId}) and will follow up once it's identified.`;

  return {
    extractedFields: [
      { key: 'client', label: 'Client', display: job.issuer },
      { key: 'scan', label: 'Scan #', display: job.jobId },
    ],
    calculation: {
      title: 'Job match',
      ruleText: 'No job number could be matched from the scan or client name, so this needs manual research, not a calculation.',
      rows: [],
      totalRow: { label: 'Status', value: 'Unmatched, needs manual research', tone: 'amber' },
    },
    drafts: [
      { key: 'ack', icon: '📧', label: 'Acknowledge → Fulfillment', to: 'fulfillment@betanxt.com', subject, body },
    ],
    approveSummary: [
      'Acknowledge the escalation to Fulfillment',
      'Log a follow-up on this scan',
      'Move this task to Done.',
    ],
    approveConfirmation: `Acknowledged to <strong>Fulfillment</strong>. Researching a job match for ${job.issuer}.`,
    closesTicket: true,
    outboundActions: [
      { key: 'ack', followUpType: 'unmatched_acknowledged', recipientEmail: 'fulfillment@betanxt.com', subject, bodyPreview: body },
    ],
  };
}

export const UNMATCHED_MATERIAL_WORKFLOW: ProxyWorkflowDefinition = {
  type: 'UNMATCHED_MATERIAL_ROUTE',
  label: 'Unmatched Material',
  noun: 'an unmatched-material review',
  trigger: 'A Fulfillment escalation reports material with no matching job number (material_status "No Job Found", escalated_to_team set).',
  source: 'Cross-section: a live Fulfillment MaterialReceiptRow (job_category "P", material_status "No Job Found", escalated_to_team set), matched to a stub Proxy job by scan number.',
  steps: ['Source & extraction', 'Job match', 'Acknowledge & approve'],
  buildTasks,
  defaultEdits,
  buildPanel,
};
